import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { getToolBySlug, tools } from '../data/tools';
import { getArticlesByTool } from '../data/articles';
import { getSystemsByTool } from '../data/systems';
import Carousel from '../components/Carousel';

const ToolBioPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navBack = useNavigate();

  if (!slug) {
    return <div>Tool not found</div>;
  }

  const tool = getToolBySlug(slug);
  const relatedArticles = getArticlesByTool(slug);
  const relatedSystems = getSystemsByTool(slug);
  const relatedTools = tool
    ? tools.filter(t => t.slug !== slug && t.categories.includes(tool.categories[0]))
    : [];

  if (!tool) {
    return (
      <div style={{ padding: '4rem 0', textAlign: 'center' }}>
        <div className="container">
          <h1>Tool Not Found</h1>
          <p>The tool you're looking for doesn't exist.</p>
          <Link to="/tool-finder" className="button button-primary">
            Browse All Tools
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '2rem 0' }}>
      <Helmet>
        <title>{tool.name} Review 2026 — Pricing, Features & Alternatives | ChooseAI</title>
        <meta name="description" content={`${tool.oneLiner}. Read our full ${tool.name} review covering pricing, key features, pros and cons, and who it’s best for.`} />
        <meta property="og:title" content={`${tool.name} Review 2026 — Pricing, Features & Alternatives | ChooseAI`} />
        <meta property="og:description" content={`${tool.oneLiner}. Read our full ${tool.name} review covering pricing, key features, pros and cons, and who it’s best for.`} />
        <meta property="og:url" content={`https://chooseai.co.uk/tools/${tool.slug}`} />
        <link rel="canonical" href={`https://chooseai.co.uk/tools/${tool.slug}`} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": tool.name,
          "description": tool.description,
          "url": tool.website,
          "applicationCategory": "BusinessApplication",
          "operatingSystem": "Web",
          "featureList": tool.featureTags.join(", "),
          "offers": tool.pricing.tiers.map(tier => ({
            "@type": "Offer",
            "name": tier.name,
            "description": tier.features.join(", "),
            "price": tier.price.match(/\d+(\.\d+)?/)?.[0] ?? "0",
            "priceCurrency": "USD"
          })),
          "review": {
            "@type": "Review",
            "reviewBody": tool.description,
            "author": { "@type": "Organization", "name": "ChooseAI", "url": "https://chooseai.co.uk" },
            "datePublished": tool.lastUpdated
          }
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://chooseai.co.uk/" },
            { "@type": "ListItem", "position": 2, "name": "ChooseTools", "item": "https://chooseai.co.uk/" },
            { "@type": "ListItem", "position": 3, "name": tool.name, "item": `https://chooseai.co.uk/tools/${tool.slug}` }
          ]
        })}</script>
      </Helmet>
      <div className="container">
        {/* Back Button */}
        <div style={{ marginBottom: 'var(--space-lg)' }}>
          <button onClick={() => navBack(-1)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', fontSize: '0.9rem', padding: 0, fontFamily: 'var(--font-primary)' }}>
            ← Back
          </button>
        </div>

        {/* Header Section */}
        <div style={{ marginBottom: '3rem' }}>
          {/* Last Updated - Top Right */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 'var(--space-md)' }}>
            <span className="text-muted" style={{ fontSize: '0.85rem' }}>
              Last updated: {new Date(tool.lastUpdated).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </span>
          </div>

          <div className="tool-bio-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-lg)', marginBottom: 'var(--space-lg)' }}>
              <img 
                src={`/logos/${tool.slug}.png`} 
                alt={`${tool.name} logo`}
                className="tool-bio-logo"
                style={{ 
                  width: '80px', 
                  height: '80px', 
                  borderRadius: '12px',
                  objectFit: 'contain',
                  backgroundColor: '#FDF8F0',
                  padding: '8px',
                  border: '1px solid var(--color-border)'
                }}
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
              <h1 style={{ margin: 0 }}>{tool.name}</h1>
            </div>
            <div className="tool-bio-meta">
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-xs)', marginBottom: 'var(--space-md)' }}>
                {tool.categories.map((category, index) => (
                  <span key={index} className="badge badge-primary" style={{ fontSize: '0.8rem' }}>
                    {category}
                  </span>
                ))}
              </div>
              <p style={{ fontSize: '1.2rem', color: 'var(--color-text-secondary)', margin: 0 }}>
                {tool.oneLiner}
              </p>
            </div>
          </div>
          <p style={{ fontSize: '1rem', marginBottom: 'var(--space-lg)' }}>
            {tool.description}
          </p>
          <div style={{ display: 'flex', gap: 'var(--space-md)', flexWrap: 'wrap' }}>
            <a href={tool.website} target="_blank" rel="noopener noreferrer" className="button button-primary">
              Visit Website
            </a>
            <span className="text-muted">
              Pricing: {tool.pricing.summary}
            </span>
          </div>
        </div>

        {/* Quick Info Grid */}
        <div className="grid grid-2" style={{ marginBottom: '0' }}>
          <div className="card">
            <h3>Key Features</h3>
            {Object.entries(tool.keyFeatureDescriptions).map(([feature, description], index) => (
              <div key={index} style={{ marginBottom: 'var(--space-md)' }}>
                <h4 style={{ fontSize: '1rem', marginBottom: 'var(--space-xs)', color: 'var(--color-primary)' }}>
                  {feature}
                </h4>
                <p style={{ fontSize: '0.9rem' }}>{description}</p>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <div className="tool-info-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', alignItems: 'stretch' }}>
              <div className="card" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center', minHeight: '200px' }}>
                <h3 style={{ marginBottom: 'var(--space-lg)' }}>Ease of Use</h3>
                <div style={{ marginBottom: 'var(--space-lg)' }}>
                  <p style={{ fontSize: '3rem', fontWeight: '700', color: 'var(--color-primary)', margin: 0, lineHeight: 1 }}>
                    {tool.easeOfUse}
                  </p>
                </div>
                <div style={{ backgroundColor: 'var(--color-bg)', border: '1px solid var(--color-border)', padding: 'var(--space-md)', borderRadius: 'var(--border-radius)', width: '100%' }}>
                  <p style={{ fontSize: '0.9rem', margin: 0, color: 'var(--color-text-primary)' }}>
                    Getting started
                  </p>
                  <p style={{ fontSize: '1.5rem', fontWeight: '600', color: 'var(--color-primary)', margin: 0 }}>
                    {tool.gettingStartedTime}
                  </p>
                </div>
              </div>
              <div className="card">
                <h3>Best For</h3>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {tool.bestFor.map((audience, index) => (
                    <li key={index} style={{ marginBottom: 'var(--space-xs)' }}>
                      <span style={{ color: 'var(--color-success)', marginRight: 'var(--space-xs)' }}>•</span>
                      {audience}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="card">
              <h3>Key Use Cases</h3>
              <ul style={{ listStyle: 'none', padding: 0 }}>
                {tool.keyUseCases.map((useCase, index) => (
                  <li key={index} style={{ marginBottom: 'var(--space-sm)', display: 'flex', alignItems: 'flex-start' }}>
                    <span style={{ color: 'var(--color-primary)', marginRight: 'var(--space-sm)', fontSize: '1.2rem' }}>→</span>
                    {useCase}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Pros and Cons */}
        <div className="grid grid-2" style={{ marginBottom: '0.5rem' }}>
          <div className="card">
            <h3>Pros</h3>
            <ul style={{ listStyle: 'none', padding: 0 }}>
              {tool.pros.map((pro, index) => (
                <li key={index} style={{ marginBottom: 'var(--space-sm)', display: 'flex', alignItems: 'flex-start' }}>
                  <span style={{ color: 'var(--color-success)', marginRight: 'var(--space-sm)', fontSize: '1.2rem' }}>✓</span>
                  {pro}
                </li>
              ))}
            </ul>
          </div>
          <div className="card">
            <h3>Cons</h3>
            <ul style={{ listStyle: 'none', padding: 0 }}>
              {tool.cons.map((con, index) => (
                <li key={index} style={{ marginBottom: 'var(--space-sm)', display: 'flex', alignItems: 'flex-start' }}>
                  <span style={{ color: 'var(--color-error)', marginRight: 'var(--space-sm)', fontSize: '1.2rem' }}>✗</span>
                  {con}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="card" style={{ marginBottom: '3rem' }}>
  <h3>Is This a Good Fit?</h3>
  <div className="grid grid-2">
    <div>
      <h4 style={{ color: 'var(--color-success)', marginBottom: 'var(--space-md)' }}>Good Fit If You:</h4>
      <ul style={{ listStyle: 'none', padding: 0 }}>
        {tool.isThisForYou.goodFit.map((fit, index) => (
          <li key={index} style={{ marginBottom: 'var(--space-sm)', display: 'flex', alignItems: 'flex-start' }}>
            <span style={{ color: 'var(--color-success)', marginRight: 'var(--space-sm)' }}>✓</span>
            {fit}
          </li>
        ))}
      </ul>
    </div>
    <div>
      <h4 style={{ color: 'var(--color-error)', marginBottom: 'var(--space-md)' }}>Not a Good Fit If You:</h4>
      <ul style={{ listStyle: 'none', padding: 0 }}>
        {tool.isThisForYou.notGoodFit.map((fit, index) => (
          <li key={index} style={{ marginBottom: 'var(--space-sm)', display: 'flex', alignItems: 'flex-start' }}>
            <span style={{ color: 'var(--color-error)', marginRight: 'var(--space-sm)' }}>✗</span>
            {fit}
          </li>
        ))}
      </ul>
    </div>
  </div>
</div>

        <div className="card" style={{ marginBottom: '3rem' }}>
          <h3>Pricing</h3>
          <div className="grid grid-3">
            {tool.pricing.tiers.map((tier, index) => (
              <div key={index} style={{ 
                border: '1px solid var(--color-border)', 
                borderRadius: 'var(--border-radius)', 
                padding: 'var(--space-md)',
                backgroundColor: 'var(--color-bg)'
              }}>
                <h4 style={{ marginBottom: 'var(--space-sm)' }}>{tier.name}</h4>
                <p style={{ fontSize: '1.2rem', fontWeight: '600', color: 'var(--color-primary)', marginBottom: 'var(--space-md)' }}>
                  {tier.price}
                </p>
                <ul style={{ listStyle: 'none', padding: 0, fontSize: '0.9rem' }}>
                  {tier.features.map((feature, featureIndex) => (
                    <li key={featureIndex} style={{ marginBottom: 'var(--space-xs)' }}>
                      <span style={{ color: 'var(--color-success)', marginRight: 'var(--space-xs)' }}>•</span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Technical Details */}
        <div className="grid grid-2" style={{ marginBottom: '3rem' }}>
  <div className="card">
    <h3>Data Privacy & Security</h3>
    <div style={{ marginBottom: 'var(--space-md)' }}>
      <h4 style={{ fontSize: '0.9rem', marginBottom: 'var(--space-xs)' }}>Data Handling:</h4>
      <p style={{ fontSize: '0.9rem' }}>{tool.dataPrivacyAndSecurity.dataHandling}</p>
    </div>
    <div style={{ marginBottom: 'var(--space-md)' }}>
      <h4 style={{ fontSize: '0.9rem', marginBottom: 'var(--space-xs)' }}>Privacy:</h4>
      <p style={{ fontSize: '0.9rem' }}>{tool.dataPrivacyAndSecurity.privacy}</p>
    </div>
    <div>
      <h4 style={{ fontSize: '0.9rem', marginBottom: 'var(--space-xs)' }}>Security Features:</h4>
      <ul style={{ listStyle: 'none', padding: 0, fontSize: '0.9rem' }}>
        {tool.dataPrivacyAndSecurity.security.map((feature, index) => (
          <li key={index} style={{ marginBottom: 'var(--space-xs)' }}>
            <span style={{ color: 'var(--color-success)', marginRight: 'var(--space-xs)' }}>•</span>
            {feature}
          </li>
        ))}
      </ul>
    </div>
  </div>
  <div className="card">
    <h3>Integrations</h3>
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', justifyContent: 'center' }}>
      {tool.integrations.map((integration, index) => (
        <div 
          key={index}
          style={{ 
            border: '1px solid var(--color-border)', 
            borderRadius: 'var(--border-radius)', 
            padding: 'var(--space-md)',
            backgroundColor: 'var(--color-bg)',
            textAlign: 'center',
            fontSize: '0.9rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '80px',
            flex: '0 0 calc(50% - 0.5rem)'
          }}>
          {integration}
        </div>
      ))}
    </div>
  </div>
</div>

        {/* Related News Carousel */}
        {relatedArticles.length > 0 && (
          <div style={{ marginBottom: '3rem' }}>
            <h3 style={{ marginBottom: 'var(--space-lg)' }}>Read More</h3>
            <Carousel
              items={relatedArticles.map((article) => ({
                preview: {
                  title: article.title,
                  subtitle: article.readTime,
                },
                content: (
                  <Link to={`/articles/${article.slug}`} style={{ textDecoration: 'none', color: 'inherit', height: '100%', display: 'flex' }}>
                    <div className="card" style={{ cursor: 'pointer', transition: 'transform var(--transition-speed), box-shadow var(--transition-speed)', height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', marginBottom: 'var(--space-md)' }}>
                        <span className="badge badge-primary">{article.category}</span>
                        <span className="text-muted" style={{ fontSize: '0.9rem' }}>{article.readTime}</span>
                      </div>
                      <h4 style={{ marginBottom: 'var(--space-md)' }}>
                        {article.title}
                      </h4>
                      <p style={{ marginBottom: 'var(--space-lg)', flex: 1 }}>{article.excerpt}</p>
                      <div style={{ textAlign: 'right' }}>
                        <span style={{ fontSize: '0.9rem', color: 'var(--color-primary)', fontWeight: '600' }}>
                          Read Article →
                        </span>
                      </div>
                    </div>
                  </Link>
                ),
              }))}
            />
          </div>
        )}

        {/* Related Tools Carousel */}
        {relatedTools.length > 0 && (
          <div style={{ marginBottom: '3rem' }}>
            <h3 style={{ marginBottom: 'var(--space-lg)' }}>Similar Tools</h3>
            <Carousel
              items={relatedTools.map((relatedTool) => ({
                preview: {
                  title: relatedTool.name,
                  subtitle: relatedTool.categories[0],
                },
                content: (
                  <Link to={`/tools/${relatedTool.slug}`} style={{ textDecoration: 'none', color: 'inherit', height: '100%', display: 'flex' }}>
                    <div className="card" style={{ cursor: 'pointer', transition: 'transform var(--transition-speed), box-shadow var(--transition-speed)', height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)', marginBottom: 'var(--space-lg)' }}>
                        <img
                          src={`/logos/${relatedTool.slug}.png`}
                          alt={`${relatedTool.name} logo`}
                          style={{ width: '64px', height: '64px', borderRadius: '12px', objectFit: 'contain', backgroundColor: '#FDF8F0', padding: '6px', border: '1px solid var(--color-border)', flexShrink: 0 }}
                          onError={e => { e.currentTarget.style.display = 'none'; }}
                        />
                        <h3 style={{ margin: 0 }}>{relatedTool.name}</h3>
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-xs)', marginBottom: 'var(--space-lg)' }}>
                        {relatedTool.categories.map((cat, i) => (
                          <span key={i} className="badge badge-primary" style={{ fontSize: '0.8rem' }}>{cat}</span>
                        ))}
                      </div>
                      <p style={{ marginBottom: 'var(--space-lg)', flex: 1, fontSize: '1rem', lineHeight: '1.6' }}>{relatedTool.oneLiner}</p>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '0.9rem', color: 'var(--color-text-muted)' }}>{relatedTool.pricing.summary}</span>
                        <span style={{ fontSize: '1rem', color: 'var(--color-primary)', fontWeight: '600' }}>View Profile →</span>
                      </div>
                    </div>
                  </Link>
                ),
              }))}
            />
          </div>
        )}

        {/* Related Systems Carousel */}
        {relatedSystems.length > 0 && (
          <div style={{ marginBottom: '3rem' }}>
            <h3 style={{ marginBottom: 'var(--space-lg)' }}>Example Systems</h3>
            <Carousel
              items={relatedSystems.map((system) => ({
                preview: {
                  title: system.name,
                  subtitle: system.oneLiner,
                },
                content: (
                  <Link to={`/flows/${system.slug}`} style={{ textDecoration: 'none', color: 'inherit', height: '100%', display: 'flex' }}>
                    <div className="card" style={{ cursor: 'pointer', transition: 'transform var(--transition-speed), box-shadow var(--transition-speed)', height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>
                      <h4 style={{ marginBottom: 'var(--space-md)' }}>{system.name}</h4>
                      <p style={{ fontSize: '0.9rem', color: 'var(--color-text-secondary)', marginBottom: 'var(--space-sm)' }}>
                        {system.oneLiner}
                      </p>
                      <p style={{ marginBottom: 'var(--space-md)', flex: 1 }}>{system.description}</p>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-xs)' }}>
                          {system.toolsUsed.slice(0, 3).map((toolSlug, index) => (
                            <span
                              key={index}
                              className="badge badge-primary"
                              style={{
                                fontSize: '0.7rem',
                              }}
                            >
                              {toolSlug}
                            </span>
                          ))}
                        </div>
                        <span style={{ fontSize: '0.9rem', color: 'var(--color-primary)', fontWeight: '600' }}>
                          View System →
                        </span>
                      </div>
                    </div>
                  </Link>
                ),
              }))}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ToolBioPage;